import { useState, useEffect, useCallback, useRef } from 'react';
import type {
  ProviderMatchingConfig,
  PatientContext,
  ProviderMatch,
  MatchingMachineState,
  MatchingResultsPayload,
  ProviderMatchingHandle,
  ProviderSelectedPayload,
} from './matchingTypes';
import type { EmbeddedToHostMessage, DateRange } from '../types';

const AWAITING_RESULTS_TIMEOUT_MS = 5_000;

export function useProviderMatching(
  config: ProviderMatchingConfig | undefined,
  patientContext: PatientContext | undefined,
  emit: (msg: EmbeddedToHostMessage) => void,
  viewRange: DateRange,
  allResourceIds: string[],
): ProviderMatchingHandle {
  const [matchingState, setMatchingState] = useState<MatchingMachineState>('IDLE');
  const [matches, setMatches] = useState<ProviderMatch[]>([]);
  const [unmatchedResourceIds, setUnmatchedResourceIds] = useState<string[]>([]);
  const [matchSummary, setMatchSummary] = useState<string | undefined>(undefined);
  const [activePatientId, setActivePatientId] = useState<string | undefined>(undefined);

  const enabled = config?.enabled ?? false;

  // ── Fire MATCHING_REQUEST on patientContext or range change ───────────────

  // Stable key: deep-compare patientContext via JSON.stringify so identity
  // changes on re-render don't cause spurious re-fires.
  const prevContextKeyRef = useRef('');
  const prevRangeKeyRef = useRef('');

  useEffect(() => {
    if (!enabled || !config || !patientContext) {
      if (!patientContext && matchingState !== 'IDLE') {
        setMatchingState('IDLE');
        setMatches([]);
        setUnmatchedResourceIds([]);
        setMatchSummary(undefined);
        setActivePatientId(undefined);
      }
      return;
    }

    const contextKey = JSON.stringify(patientContext);
    const rangeKey = `${viewRange.startUtc}|${viewRange.endUtc}|${allResourceIds.join(',')}`;

    const contextChanged = contextKey !== prevContextKeyRef.current;
    const rangeChanged = rangeKey !== prevRangeKeyRef.current;

    // Re-request when already active and the range changed, or whenever context changes.
    const shouldRequest =
      contextChanged ||
      (rangeChanged && matchingState === 'MATCHING_ACTIVE');

    if (!shouldRequest) {
      // Update range key so the next change is detected correctly.
      prevRangeKeyRef.current = rangeKey;
      return;
    }

    prevContextKeyRef.current = contextKey;
    prevRangeKeyRef.current = rangeKey;

    const payload = {
      patientContext,
      visibleRangeStart: viewRange.startUtc,
      visibleRangeEnd: viewRange.endUtc,
      resourceIds: allResourceIds,
    };

    setMatchingState('AWAITING_RESULTS');

    const result = config.onRequestMatching(payload);
    emit({ type: 'MATCHING_REQUEST', payload });

    // Direct-React mode: callback returns Promise<MatchingResultsPayload>.
    if (result && typeof (result as Promise<MatchingResultsPayload>).then === 'function') {
      let cancelled = false;
      (result as Promise<MatchingResultsPayload>)
        .then((r) => {
          if (!cancelled) applyResults(r);
        })
        .catch(() => { if (!cancelled) setMatchingState('IDLE'); });
      return () => { cancelled = true; };
    }

    // iframe/postMessage mode: 5-second timeout if no MATCHING_RESULTS arrives.
    const timerId = setTimeout(() => {
      setMatchingState((prev) => prev === 'AWAITING_RESULTS' ? 'IDLE' : prev);
    }, AWAITING_RESULTS_TIMEOUT_MS);

    return () => clearTimeout(timerId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, JSON.stringify(patientContext), viewRange.startUtc, viewRange.endUtc, allResourceIds.join(','), matchingState]);

  // ── Apply results (called from both direct-React and postMessage paths) ───

  const applyResults = useCallback((results: MatchingResultsPayload) => {
    setMatches(results.matches.slice().sort((a, b) => b.matchScore - a.matchScore));
    setUnmatchedResourceIds(results.unmatchedResourceIds);
    setMatchSummary(results.matchSummary);
    setActivePatientId(results.patientId);
    setMatchingState('MATCHING_ACTIVE');
  }, []);

  // ── Handle inbound MATCHING_RESULTS from postMessage ─────────────────────
  // Exposed via ref so ScheduleContext can call it without a TDZ issue.

  const applyMatchingResults = useCallback((results: MatchingResultsPayload) => {
    applyResults(results);
  }, [applyResults]);

  // ── Provider selection ────────────────────────────────────────────────────

  const handleProviderSelected = useCallback((resourceId: string) => {
    if (matchingState !== 'MATCHING_ACTIVE' || !config) return;
    const match = matches.find((m) => m.resourceId === resourceId);
    const isMatched = !!match;
    const currentPatientId = activePatientId ?? '';

    const payload: ProviderSelectedPayload = {
      resourceId,
      patientId: currentPatientId,
      matchScore: match?.matchScore ?? 0,
      matchLevel: match?.matchLevel ?? 'weak',
      selectedFrom: isMatched ? 'matched' : 'unmatched',
    };

    config.onProviderSelected?.(payload);
    emit({ type: 'PROVIDER_SELECTED', payload });
  }, [matchingState, matches, activePatientId, config, emit]);

  // ── Clear matching ────────────────────────────────────────────────────────

  const clearMatching = useCallback(() => {
    if (matchingState === 'IDLE' || !activePatientId) return;
    emit({ type: 'MATCHING_CLEARED', payload: { patientId: activePatientId } });
    setMatchingState('IDLE');
    setMatches([]);
    setUnmatchedResourceIds([]);
    setMatchSummary(undefined);
    setActivePatientId(undefined);
    // Reset context key so re-enabling matching with the same context fires a request.
    prevContextKeyRef.current = '';
  }, [matchingState, activePatientId, emit]);

  return {
    matchingState,
    matches,
    unmatchedResourceIds,
    matchSummary,
    activePatientId,
    handleProviderSelected,
    clearMatching,
    applyMatchingResults,
  };
}
